import { useState, useEffect, useRef } from 'react';
import React from 'react';
import { SEO } from '../../utils/seo';

function useSectionReveal() {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('is-visible');
          observer.unobserve(el);
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

type SectionRevealProps = {
  children: React.ReactNode;
  className?: string;
  as?: 'section' | 'div';
  [key: string]: any;
};

function SectionReveal({
  children,
  className = '',
  as = 'section',
  ...props
}: SectionRevealProps) {
  const ref = useSectionReveal();
  const Tag = as;
  return (
    <Tag ref={ref} className={`section-reveal ${className}`} {...props}>
      {children}
    </Tag>
  );
}

function QuickContactForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      setStatus('idle');
      return;
    }

    try {
      const response = await fetch(
        'https://readdy.ai/api/form/d63ffv9ov5qgbon31u1g',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams(formData as any).toString(),
        }
      );

      if (response.ok) {
        setStatus('success');
        form.reset();
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 5000);
      }
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  if (status === 'success') {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center py-8">
        <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 rounded-full mb-4">
          <i className="ri-check-line text-3xl text-emerald-700"></i>
        </div>
        <h4 className="text-xl font-bold mb-2">Message Sent!</h4>
        <p className="text-gray-600">We&apos;ll get back to you shortly.</p>
      </div>
    );
  }

  return (
    <form
      className="space-y-4"
      data-readdy-form
      id="quick-contact-form"
      onSubmit={handleSubmit}
    >
      <div>
        <label htmlFor="quickName" className="block text-sm font-bold mb-2">
          Name <span className="text-red-600">*</span>
        </label>
        <input
          type="text"
          id="quickName"
          name="name"
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-700 focus:border-transparent text-sm"
          placeholder="Your name"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="quickEmail" className="block text-sm font-bold mb-2">
            Email <span className="text-red-600">*</span>
          </label>
          <input
            type="email"
            id="quickEmail"
            name="email"
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-700 focus:border-transparent text-sm"
            placeholder="you@email.com"
          />
        </div>
        <div>
          <label htmlFor="quickPhone" className="block text-sm font-bold mb-2">
            Phone
          </label>
          <input
            type="tel"
            id="quickPhone"
            name="phone"
            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-700 focus:border-transparent text-sm"
            placeholder="(555) 123-4567"
          />
        </div>
      </div>

      <div>
        <label htmlFor="quickMessage" className="block text-sm font-bold mb-2">
          Message <span className="text-red-600">*</span>
        </label>
        <textarea
          id="quickMessage"
          name="message"
          rows={3}
          required
          maxLength={500}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-700 focus:border-transparent resize-none text-sm"
          placeholder="What can we help you with?"
        ></textarea>
      </div>

      {status === 'error' && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md text-sm">
          Something went wrong. Please try again.
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full bg-emerald-800 hover:bg-emerald-700 text-white font-bold px-6 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed text-sm"
      >
        {status === 'submitting' ? 'Sending...' : 'Send Message'}
      </button>
    </form>
  );
}

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: "How much does aerial application cost compared to ground spraying?",
      answer: "Pricing varies based on acreage, product type, and field conditions, but most operations find aerial application competitive with ground spraying when you factor in the hidden costs of compaction damage, crop destruction, and missed timing windows. On average, ground equipment destroys 3-5% of standing crop per pass—that's $27,000-$45,000 lost on 1,000 acres of corn. We're happy to walk through the numbers for your specific operation."
    },
    {
      question: "What's the minimum acreage you'll work with?",
      answer: "We typically work with operations of 200 acres or more for aerial application services. For smaller acreages, we can often coordinate with neighboring farms to make it work. Deer deterrent and land management services have more flexibility on minimum size. Give us a call—we'll be straight with you about whether it makes sense."
    },
    {
      question: "Can you spray when my fields are too wet for ground equipment?",
      answer: "Yes—that's one of the biggest advantages. We can apply when fields are saturated and ground rigs would cause ruts, compaction, or get stuck entirely. This means you don't miss critical spray windows after rain events. We've saved many crops by getting fungicide on during narrow windows when ground equipment simply couldn't enter the field."
    },
    {
      question: "What products can you apply? Do you handle my chemicals or do I provide them?",
      answer: "We can apply fungicides, herbicides, foliar nutrients, plant growth regulators, and cover crop seed. You can provide your own products, or we can source them for you—whatever works best for your operation. We'll work with your agronomist or retailer to make sure everything is coordinated properly."
    },
    {
      question: "How do you handle drift and ensure accurate application?",
      answer: "Our equipment uses precision GPS guidance and variable rate technology for accurate placement. We fly at lower altitudes than traditional aircraft, which significantly reduces drift. We also monitor wind conditions closely and won't fly when conditions aren't right. Buffer zones and sensitive areas are mapped before we start, and we take drift prevention seriously—your neighbors are often our customers too."
    },
    {
      question: "How quickly can you respond when I have a narrow spray window?",
      answer: "During peak season, we maintain crews ready to mobilize within 24-48 hours for urgent applications. We understand that a 3-day delay during critical growth stages can cost you $60-90 per acre in yield loss. That's why we prioritize responsiveness and maintain the capacity to move quickly when weather windows open up."
    },
    {
      question: "Does aerial spraying actually work as well as ground application?",
      answer: "For most applications, yes—and often better. Aerial application provides more uniform coverage, especially in tall crops where ground equipment can't achieve good canopy penetration. University studies show equivalent or superior efficacy for fungicides and foliar nutrients. The key difference is we're not destroying 3-5% of your crop to apply the product."
    },
    {
      question: "What about deer damage—how does your deterrent program work?",
      answer: "Our deer deterrent program uses a combination of aerial thermal surveys to identify pressure points, targeted deterrent applications, and ongoing monitoring. We focus on the field edges where deer do the most damage. Most operations see 50-70% reduction in browse damage. At $800+ per acre in edge-row losses, the program typically pays for itself several times over."
    },
    {
      question: "I'm skeptical about drones—is this actually proven technology?",
      answer: "We understand the skepticism. Agricultural drones have been used commercially for over a decade, with millions of acres treated annually in the US alone. The technology is FAA-certified, and our operators are licensed and insured. We're not asking you to be an early adopter—we're offering proven equipment and methods that are already delivering results for operations like yours across the region."
    },
    {
      question: "How do I know if aerial application makes sense for my operation?",
      answer: "The best way is to have a conversation. Tell us about your acres, what you're growing, your biggest challenges, and what you've tried before. We'll give you an honest assessment of whether our services would help. If it's not a good fit, we'll tell you. No pressure, no sales pitch—just straight answers about what we can and can't do for your specific situation."
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <SectionReveal className="py-28 px-6 bg-stone-50">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <span className="inline-block text-emerald-700 font-bold text-xs tracking-widest uppercase mb-4">
            Common Questions
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-wide">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto leading-relaxed font-light">
            Straight answers to the questions farmers and landowners ask us most.
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <span className="font-bold text-gray-900 leading-snug">
                  {faq.question}
                </span>
                <div className="w-6 h-6 flex items-center justify-center shrink-0">
                  <i
                    className={`ri-arrow-down-s-line text-2xl text-emerald-700 transition-transform duration-300 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  ></i>
                </div>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-6 pb-5 text-gray-700 leading-relaxed border-t border-gray-100 pt-4">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">
            Have a question we didn't answer?
          </p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 bg-emerald-800 hover:bg-emerald-700 text-white font-bold px-8 py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-chat-3-line"></i>
            Ask Us Directly
          </a>
        </div>
      </div>
    </SectionReveal>
  );
}

export default function HomePage() {
  const [formStatus, setFormStatus] = useState<
    'idle' | 'submitting' | 'success' | 'error'
  >('idle');

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Greenfield Partners",
    "description": "Precision aerial application and land management for row-crop farmers",
    "url": siteUrl,
    "logo": `${siteUrl}/vite.svg`,
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-717-913-1780",
      "contactType": "Customer Service",
      "areaServed": "US"
    },
    "sameAs": [],
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "US"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "reviewCount": "6"
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const farmDetails = formData.get('farmDetails') as string;
    if (farmDetails && farmDetails.length > 500) {
      setFormStatus('idle');
      return;
    }

    try {
      const response = await fetch(
        'https://readdy.ai/api/form/d63akbibdoed7v0v8meg',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams(formData as any).toString(),
        }
      );

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Greenfield Partners - Precision Aerial Application for Row-Crop Farmers"
        description="Greenfield Partners helps row-crop farmers maximize yield through precision aerial application and land management. Reduce crop damage, save time, and protect your return per acre."
        keywords="precision agriculture, aerial crop application, yield protection, row crop farming, deer damage mitigation, compaction reduction, timely application"
        schema={schema}
      />
      {/* ─── Hero ─── */}
      <section className="relative h-screen min-h-[600px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/f53427c02b00678782e68874888578ce/9975176f1026b420c5e1da8dd0a2f61d.png"
            alt="Evolution of agriculture - traditional tractor sprayer transitioning to modern precision drone technology"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/70 via-emerald-900/40 to-emerald-950/50"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-none tracking-wide text-white">
              The Future<br />
              of Fieldwork<br />
              Is Here.
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-xl leading-relaxed font-light text-gray-200">
              Precision aerial application that reduces crop damage, saves time
              during critical windows, and protects your yield when it matters
              most.
            </p>
            <p className="text-lg mb-10 text-gray-300 font-light tracking-wide max-w-lg">
              Less compaction. Better timing. More bushels per acre.
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <a
                href="#contact"
                className="inline-block bg-emerald-800 hover:bg-emerald-700 text-white font-bold px-10 py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap text-lg tracking-wide"
              >
                Talk With Us About Your Fields
              </a>
              <a
                href="/roi-calculator"
                className="inline-flex items-center gap-2 border-2 border-white/40 hover:bg-white/10 text-white font-bold px-8 py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap text-lg tracking-wide"
              >
                <i className="ri-calculator-line"></i>
                Calculate Your Savings
              </a>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-stone-50 to-transparent z-10"></div>
      </section>

      {/* Services Section - Add links to relevant pages */}
      <section className="py-20 px-6 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block text-emerald-700 font-bold text-xs tracking-widest uppercase mb-4">
              Our Services
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-wide">
              What We Do
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed font-light">
              We help row-crop farmers and landowners protect yield through
              precision aerial application and land management. No wheel traffic.
              No compaction. No broken stalks. Just timely, effective coverage
              when your crops need it most.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Precision Application Card */}
            <div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 flex items-center justify-center mb-4">
                <i className="ri-plant-line text-5xl text-emerald-800"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 tracking-wide">
                Precision Aerial Application
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Apply fungicides, nutrients, and crop protection products without wheel traffic. Zero compaction, no stand loss, and access to fields when ground equipment can't get in.
              </p>
              <a
                href="/seasonal-programs"
                className="inline-flex items-center text-emerald-800 font-semibold hover:text-emerald-700 transition-colors cursor-pointer text-sm"
              >
                View Seasonal Programs
                <i className="ri-arrow-right-line text-lg ml-1"></i>
              </a>
            </div>

            {/* Field Intelligence Card */}
            <div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-5xl text-emerald-800"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 tracking-wide">
                Yield Protection Services
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                <a href="/services/deer-deterrent" className="text-emerald-800 hover:text-emerald-700 font-medium">Deer damage mitigation</a>, invasive species control, and overgrowth management to protect every acre from field edge to field edge.
              </p>
              <a
                href="/services/deer-deterrent"
                className="inline-flex items-center text-emerald-800 font-semibold hover:text-emerald-700 transition-colors cursor-pointer text-sm"
              >
                Protect Your Yield
                <i className="ri-arrow-right-line text-lg ml-1"></i>
              </a>
            </div>

            {/* Timely Application Windows Card */}
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <div className="w-14 h-14 flex items-center justify-center mb-4">
                <i className="ri-time-line text-5xl text-emerald-800"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 tracking-wide">
                Timely Application Windows
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Weather doesn't wait. Neither should you. We can apply when
                ground equipment can't, keeping you on schedule during
                critical growth stages.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Timing matters. Delays cost yield. We help you stay ahead.
              </p>
            </div>

            {/* Field Assessment Card */}
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <div className="w-14 h-14 flex items-center justify-center mb-4">
                <i className="ri-eye-line text-5xl text-emerald-800"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 tracking-wide">
                Field Assessment
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Aerial views that help you spot drainage issues, stand problems,
                pest pressure, and crop stress before they cost you bushels.
              </p>
              <p className="text-gray-700 leading-relaxed">
                See your fields the way they really are, not just from the road.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Stats ─── */}
      <SectionReveal className="py-28 px-6 topo-texture-light bg-gradient-to-b from-stone-100/40 via-stone-100/70 to-stone-100/40">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <span className="inline-block text-emerald-700 font-bold text-xs tracking-widest uppercase mb-4">
              Proven Results
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-wide">
              Real Numbers From Real Fields
            </h2>
            <p className="text-xl text-gray-700 font-light">
              These aren't promises. They're results from farmers who've
              made the switch.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-arrow-up-line text-6xl text-emerald-800"></i>
              </div>
              <div className="text-4xl font-bold text-emerald-800 mb-2">5–15%</div>
              <p className="text-gray-700 font-semibold">Yield improvements from timely application</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-check-line text-6xl text-emerald-800"></i>
              </div>
              <div className="text-4xl font-bold text-emerald-800 mb-2">3–7 Days</div>
              <p className="text-gray-700 font-semibold">Saved per season by avoiding wet-field delays</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-leaf-line text-6xl text-emerald-800"></i>
              </div>
              <div className="text-4xl font-bold text-emerald-800 mb-2">Up to 80%</div>
              <p className="text-gray-700 font-semibold">Reduction in compaction and crop damage</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-seedling-line text-6xl text-emerald-800"></i>
              </div>
              <div className="text-4xl font-bold text-emerald-800 mb-2">7–15%</div>
              <p className="text-gray-700 font-semibold">Yield increase from optimized fungicide timing</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-dollar-circle-line text-6xl text-emerald-800"></i>
              </div>
              <div className="text-4xl font-bold text-emerald-800 mb-2">$8–$15</div>
              <p className="text-gray-700 font-semibold">Per acre saved from reduced field passes</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-star-line text-6xl text-emerald-800"></i>
              </div>
              <div className="text-4xl font-bold text-emerald-800 mb-2">10–20%</div>
              <p className="text-gray-700 font-semibold">Yield protection in areas with deer pressure</p>
            </div>
          </div>

          <div className="mt-12 bg-white border border-red-200 rounded-lg overflow-hidden shadow-sm">
            <div className="px-6 py-3 bg-red-50/70 border-b border-red-100 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-error-warning-fill text-red-500 text-lg"></i>
              </div>
              <span className="text-sm font-bold text-gray-800 tracking-wide uppercase">What inaction costs — every season</span>
            </div>
            <div className="grid md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-red-100">
              <div className="px-6 py-5 text-center">
                <div className="text-2xl font-bold text-red-600 mb-1">$27K–$45K</div>
                <p className="text-xs text-gray-600">Lost to wheel traffic damage per 1,000 acres</p>
              </div>
              <div className="px-6 py-5 text-center">
                <div className="text-2xl font-bold text-red-600 mb-1">$180/acre</div>
                <p className="text-xs text-gray-600">Yield loss from a single missed spray window</p>
              </div>
              <div className="px-6 py-5 text-center">
                <div className="text-2xl font-bold text-red-600 mb-1">$800+/acre</div>
                <p className="text-xs text-gray-600">Annual deer browse damage on unprotected edges</p>
              </div>
              <div className="px-6 py-5 text-center">
                <div className="text-2xl font-bold text-red-600 mb-1">$50–$120</div>
                <p className="text-xs text-gray-600">Per acre lost yearly to undetected drainage issues</p>
              </div>
            </div>
            <div className="px-6 py-3 bg-emerald-50/60 border-t border-emerald-100 text-center">
              <p className="text-sm text-emerald-800 font-medium">
                <i className="ri-shield-check-line mr-1"></i>
                We handle the solution end-to-end — so you can focus on farming, not fixing.
              </p>
            </div>
          </div>
        </div>
      </SectionReveal>

      {/* ROI Calculator CTA Strip */}
      <SectionReveal className="py-16 px-6 bg-stone-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white border border-gray-200 rounded-lg p-8 md:p-10 shadow-sm">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-16 h-16 flex items-center justify-center bg-emerald-50 rounded-full shrink-0">
                <i className="ri-calculator-line text-3xl text-emerald-700"></i>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-2xl font-bold mb-2 tracking-wide">
                  What Could Your Acres Be Earning?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Enter your farm details and see a personalized estimate of potential yield recovery and cost savings. Takes less than 2 minutes.
                </p>
              </div>
              <a
                href="/roi-calculator"
                className="inline-flex items-center gap-2 bg-emerald-800 hover:bg-emerald-700 text-white font-bold px-8 py-4 rounded-lg transition-colors cursor-pointer whitespace-nowrap shrink-0"
              >
                <i className="ri-calculator-line"></i>
                Calculate My Savings
              </a>
            </div>
          </div>
        </div>
      </SectionReveal>

      {/* ─── Why It Matters ─── */}
      <SectionReveal className="py-28 px-6 bg-stone-50 topo-texture-light">
        <div className="max-w-6xl mx-auto relative z-10 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block text-emerald-700 font-bold text-xs tracking-widest uppercase mb-4">
              The Difference
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-wide">
              Why It Matters
            </h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold mb-3 text-emerald-800 tracking-wide">Less Damage, More Yield</h3>
                <p className="text-gray-700 leading-relaxed">
                  Every pass through the field with ground equipment damages plants, compacts soil, and costs you bushels. Aerial application eliminates that loss entirely.
                </p>
                <p className="mt-2 text-sm text-gray-600 italic border-l-2 border-red-400 pl-3">
                  Soil compaction alone reduces root depth by up to 40%, costing <strong className="text-red-600 not-italic">$15–$30/acre</strong> in hidden yield drag — year after year.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold mb-3 text-emerald-800 tracking-wide">Timing Is Everything</h3>
                <p className="text-gray-700 leading-relaxed">
                  When fields are too wet or crops are too tall, waiting means missing the window. We can apply when you need it, not when the ground finally dries out.
                </p>
                <p className="mt-2 text-sm text-gray-600 italic border-l-2 border-red-400 pl-3">
                  A <strong className="text-red-600 not-italic">3-day delay</strong> during R3 soybean stage can mean <strong className="text-red-600 not-italic">$60–$90/acre</strong> in preventable loss. The window doesn't reopen.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold mb-3 text-emerald-800 tracking-wide">Your Land, Your Return</h3>
                <p className="text-gray-700 leading-relaxed">
                  We care about your land and yields as much as you do. This isn't about flying acres—it's about protecting what you've invested in all season long.
                </p>
                <p className="mt-2 text-sm text-gray-600 italic border-l-2 border-red-400 pl-3">
                  Replanting a failed field costs <strong className="text-red-600 not-italic">$80–$150/acre</strong> — and you still lose the season. Prevention is always cheaper than recovery.
                </p>
              </div>
            </div>
          </div>

          <div className="w-full h-[520px] rounded-lg overflow-hidden shadow-md">
            <img
              src="https://public.readdy.ai/ai/img_res/6bb2b602164998943f22d1fabeb233a8.jpg"
              alt="Healthy undamaged corn crop"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </SectionReveal>

      {/* ─── How We Work ─── */}
      <SectionReveal className="py-28 px-6 bg-gradient-to-b from-stone-100/40 via-stone-100/70 to-stone-100/40 topo-texture-light">
        <div className="max-w-6xl mx-auto relative z-10">
          <span className="block text-center text-emerald-700 font-bold text-xs tracking-widest uppercase mb-4">
            Our Process
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center tracking-wide">
            How We Work With You
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <div className="text-5xl font-bold text-emerald-800 mb-4 tracking-tight">1</div>
              <h3 className="text-2xl font-bold mb-4 tracking-wide">Start a Conversation</h3>
              <p className="text-gray-700 leading-relaxed">
                Tell us about your operation, your fields, and what you&apos;re trying to protect. No pressure, no sales pitch—just a practical conversation about whether this makes sense for you.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm">
              <div className="text-5xl font-bold text-emerald-800 mb-4 tracking-tight">2</div>
              <h3 className="text-2xl font-bold mb-4 tracking-wide">Plan Together</h3>
              <p className="text-gray-700 leading-relaxed">
                We&apos;ll walk your fields, understand your timing, and build a plan that fits your operation. You know your land better than anyone—we&apos;re here to support what you&apos;re already doing.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm">
              <div className="text-5xl font-bold text-emerald-800 mb-4 tracking-tight">3</div>
              <h3 className="text-2xl font-bold mb-4 tracking-wide">Get It Done Right</h3>
              <p className="text-gray-700 leading-relaxed">
                When the time comes, we&apos;ll be ready. Fast, precise application when your crops need it most. Then we&apos;ll follow up to make sure everything went as planned.
              </p>
            </div>
          </div>
        </div>
      </SectionReveal>

      {/* Quick Contact Form */}
      <SectionReveal className="py-20 px-6 bg-stone-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="grid md:grid-cols-[1fr_1.2fr]">
              <div className="bg-emerald-800 p-8 md:p-10 text-white flex flex-col justify-center">
                <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-full mb-6">
                  <i className="ri-chat-3-line text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold mb-3 tracking-wide">Have a Quick Question?</h3>
                <p className="text-emerald-100 leading-relaxed mb-6">
                  Drop us a message and we&apos;ll get back to you within 24 hours. No commitment required.
                </p>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-check-line text-emerald-300"></i>
                    </div>
                    <span className="text-emerald-100">Fast response time</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-check-line text-emerald-300"></i>
                    </div>
                    <span className="text-emerald-100">No obligation</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-check-line text-emerald-300"></i>
                    </div>
                    <span className="text-emerald-100">Straight answers</span>
                  </div>
                </div>
              </div>
              <div className="p-8 md:p-10">
                <QuickContactForm />
              </div>
            </div>
          </div>
        </div>
      </SectionReveal>

      {/* Trust / Philosophy */}
      <SectionReveal className="py-28 px-6 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white p-12 rounded-lg shadow-sm">
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-block text-emerald-700 font-bold text-xs tracking-widest uppercase mb-4">
                Our Philosophy
              </span>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-wide">
                Built on Trust, Not Hype
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed mb-6 font-light">
                We&apos;re not here to sell you on technology. We&apos;re here to help you protect yield, save time, and get more return from every acre you farm.
              </p>
              <p className="text-xl text-gray-700 leading-relaxed mb-6 font-light">
                We work with farmers, not over them. We understand timing, weather windows, and what it takes to get a crop from planting to harvest.
              </p>
              <p className="text-xl text-gray-700 leading-relaxed font-semibold tracking-wide">
                Long-term productivity beats short-term fixes. Every time.
              </p>
            </div>
          </div>
        </div>
      </SectionReveal>

      {/* What's at Stake Strip */}
      <SectionReveal className="py-16 px-6 bg-stone-50">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            <div className="grid md:grid-cols-[280px_1fr] items-stretch">
              <div className="w-full h-[220px] md:h-auto overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Damaged%20corn%20field%20with%20broken%20stalks%20and%20visible%20wheel%20ruts%20from%20heavy%20ground%20sprayer%20equipment%2C%20muddy%20tire%20tracks%20through%20crop%20rows%2C%20overcast%20sky%2C%20realistic%20agricultural%20damage%20photography%20showing%20compaction%20and%20crop%20loss%20in%20Midwest%20farmland&width=560&height=400&seq=damage-cost-001&orientation=landscape"
                  alt="Crop damage from ground equipment compaction"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-error-warning-fill text-red-500 text-lg"></i>
                  </div>
                  <span className="text-xs font-bold text-red-600 tracking-widest uppercase">The quiet cost of waiting</span>
                </div>
                <p className="text-gray-800 leading-relaxed mb-4">
                  Most yield loss doesn&apos;t happen in a single event. It accumulates — <strong className="text-red-600">a missed window here, compaction damage there, deer pressure on the edges</strong>. By harvest, the gap between what you grew and what you could have grown is often <strong className="text-red-600">$40–$130+ per acre</strong>.
                </p>
                <p className="text-sm text-emerald-800 font-medium">
                  <i className="ri-shield-check-line mr-1"></i>
                  We exist to close that gap. One season at a time, one field at a time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </SectionReveal>

      {/* FAQ Section */}
      <FAQSection />

      {/* ─── Testimonials ─── */}
      <SectionReveal className="py-28 px-6 topo-texture-light bg-gradient-to-b from-stone-100/40 via-stone-100/70 to-stone-100/40">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <span className="inline-block text-emerald-700 font-bold text-xs tracking-widest uppercase mb-4">
              Testimonials
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-wide">
              Trusted by Operations Across the Region
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
              Don&apos;t take our word for it. Here&apos;s what the farmers we work with have to say.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-star-fill text-lg text-emerald-700"></i>
                  </div>
                ))}
              </div>
              <p className="text-gray-700 leading-relaxed mb-6">
                &quot;We switched to aerial application two seasons ago and haven&apos;t looked back. No more ruts in wet fields, no more waiting three days after a rain. They got our fungicide on during the exact window we needed and we saw a real difference at harvest.&quot;
              </p>
              <div className="flex items-center gap-4">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20middle-aged%20male%20farmer%20wearing%20casual%20work%20shirt%20outdoors%20natural%20lighting%20confident%20friendly%20expression%20rural%20American%20farmer%20authentic%20documentary%20style%20photography&width=120&height=120&seq=farmer-mark-001&orientation=squarish"
                  alt="Mark Hendricks"
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-bold text-gray-900">Mark Hendricks</p>
                  <p className="text-sm text-gray-500">2,400 acres · Corn &amp; Soybeans · Lancaster County, PA</p>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-star-fill text-lg text-emerald-700"></i>
                  </div>
                ))}
              </div>
              <p className="text-gray-700 leading-relaxed mb-6">
                &quot;The deer were eating us alive on the north fields. We&apos;d tried fencing, repellents, everything. Their mitigation program cut our edge-row losses by at least half. That alone paid for the service twice over.&quot;
              </p>
              <div className="flex items-center gap-4">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20female%20farmer%20in%20her%20fifties%20wearing%20plaid%20shirt%20outdoors%20natural%20lighting%20warm%20smile%20confident%20expression%20rural%20Wisconsin%20farmer%20authentic%20documentary%20style%20photography&width=120&height=120&seq=farmer-janet-001&orientation=squarish"
                  alt="Janet Kowalski"
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-bold text-gray-900">Janet &amp; Dale Kowalski</p>
                  <p className="text-sm text-gray-500">1,100 acres · Corn &amp; Wheat · Frederick County, MD</p>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-star-fill text-lg text-emerald-700"></i>
                  </div>
                ))}
              </div>
              <p className="text-gray-700 leading-relaxed mb-6">
                &quot;I was skeptical at first—figured it was just another tech pitch. But these guys actually understand farming. They walked my fields, asked the right questions, and delivered exactly what they said they would. Straightforward and honest.&quot;
              </p>
              <div className="flex items-center gap-4">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20older%20male%20farmer%20in%20his%20sixties%20wearing%20baseball%20cap%20and%20work%20jacket%20outdoors%20natural%20lighting%20serious%20trustworthy%20expression%20Iowa%20farmer%20authentic%20documentary%20style%20photography&width=120&height=120&seq=farmer-tom-001&orientation=squarish"
                  alt="Tom Reinhardt"
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-bold text-gray-900">Tom Reinhardt</p>
                  <p className="text-sm text-gray-500">3,800 acres · Soybeans &amp; Corn · Sussex County, DE</p>
                </div>
              </div>
            </div>

            {/* Testimonial 4 */}
            <div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-star-fill text-lg text-emerald-700"></i>
                  </div>
                ))}
              </div>
              <p className="text-gray-700 leading-relaxed mb-6">
                &quot;Last July we had a narrow spray window between storms. Ground rigs couldn&apos;t get in. They had our entire 900 acres covered in a day and a half. That timing saved our soybean crop from white mold. Period.&quot;
              </p>
              <div className="flex items-center gap-4">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20young%20female%20farmer%20in%20her%20thirties%20wearing%20work%20shirt%20outdoors%20natural%20lighting%20confident%20determined%20expression%20Missouri%20farmer%20authentic%20documentary%20style%20photography&width=120&height=120&seq=farmer-sarah-001&orientation=squarish"
                  alt="Sarah Brennan"
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-bold text-gray-900">Sarah Brennan</p>
                  <p className="text-sm text-gray-500">900 acres · Soybeans · Kent County, DE</p>
                </div>
              </div>
            </div>

            {/* Testimonial 5 */}
            <div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-star-fill text-lg text-emerald-700"></i>
                  </div>
                ))}
              </div>
              <p className="text-gray-700 leading-relaxed mb-6">
                &quot;We manage timber and row crops. They handle both sides for us—aerial spraying on the fields and invasive species control in the timber. Having one team that understands the whole property makes a huge difference.&quot;
              </p>
              <div className="flex items-center gap-4">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20middle-aged%20male%20farmer%20wearing%20flannel%20shirt%20outdoors%20natural%20lighting%20friendly%20approachable%20expression%20Indiana%20mixed%20operation%20farmer%20authentic%20documentary%20style%20photography&width=120&height=120&seq=farmer-greg-001&orientation=squarish"
                  alt="Greg Meyers"
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-bold text-gray-900">Greg &amp; Linda Meyers</p>
                  <p className="text-sm text-gray-500">1,600 acres · Mixed Operation · Eastern Panhandle, WV</p>
                </div>
              </div>
            </div>

            {/* Testimonial 6 */}
            <div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-star-fill text-lg text-emerald-700"></i>
                  </div>
                ))}
              </div>
              <p className="text-gray-700 leading-relaxed mb-6">
                &quot;The field assessment alone was worth it. They showed me drainage issues I&apos;d been guessing about for years. We fixed two problem spots and picked up an easy 8 bushels per acre in those zones this fall.&quot;
              </p>
              <div className="flex items-center gap-4">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20older%20male%20farmer%20in%20his%20late%20fifties%20wearing%20denim%20shirt%20outdoors%20natural%20lighting%20experienced%20wise%20expression%20Indiana%20corn%20farmer%20authentic%20documentary%20style%20photography&width=120&height=120&seq=farmer-ray-001&orientation=squarish"
                  alt="Ray Caldwell"
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-bold text-gray-900">Ray Caldwell</p>
                  <p className="text-sm text-gray-500">2,000 acres · Corn · Shenandoah Valley, VA</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </SectionReveal>

      {/* Gradient bridge into dark contact section */}
      <div className="h-40 bg-gradient-to-b from-stone-50 via-stone-200/40 to-[#1a1f1c]"></div>

      {/* ─── Contact Form ─── */}
      <section
        className="py-24 px-6 topo-cta-bg text-white relative"
        id="contact"
      >
        <div className="relative z-10 max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
          <div>
            <span className="inline-block text-emerald-400 font-bold text-xs tracking-widest uppercase mb-4">
              Get In Touch
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-wide">
              Let&apos;s Talk About Your Operation
            </h2>
            <p className="text-xl text-gray-300 mb-6 leading-relaxed font-light">
              No pressure. No sales pitch. Just a practical conversation about your fields, your challenges, and whether our services make sense for your operation.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-6 font-light">
              Tell us about your acres, what you&apos;re growing, and what you&apos;re trying to protect. We&apos;ll give you straight answers about what we can do and what we can&apos;t.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed font-light">
              If it&apos;s a good fit, we&apos;ll work together. If it&apos;s not, we&apos;ll tell you that too.
            </p>
          </div>

          <div className="bg-white text-gray-900 p-8 rounded-lg">
            <form
              className="space-y-4"
              data-readdy-form
              id="consultation-form"
              onSubmit={handleSubmit}
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-bold mb-2">
                    First Name <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-700 focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-bold mb-2">
                    Last Name <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-700 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-bold mb-2">
                  Email <span className="text-red-600">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-700 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-bold mb-2">
                  Phone Number <span className="text-red-600">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-700 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="farmSize" className="block text-sm font-bold mb-2">
                  How many acres do you farm? <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="farmSize"
                  name="farmSize"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-700 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="cropType" className="block text-sm font-bold mb-2">
                  What do you grow?
                </label>
                <input
                  type="text"
                  id="cropType"
                  name="cropType"
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-700 focus:border-transparent"
                  placeholder="Corn, soybeans, wheat, etc."
                />
              </div>

              <div>
                <label htmlFor="farmDetails" className="block text-sm font-bold mb-2">
                  Tell us about your operation
                </label>
                <textarea
                  id="farmDetails"
                  name="farmDetails"
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-700 focus:border-transparent resize-none"
                  placeholder="What challenges are you facing? What are you trying to protect?"
                ></textarea>
                <p className="text-xs text-gray-600 mt-1">Maximum 500 characters</p>
              </div>

              {formStatus === 'success' && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md">
                  Thanks for reaching out. We&apos;ll be in touch soon.
                </div>
              )}

              {formStatus === 'error' && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md">
                  Something went wrong. Please try again or give us a call.
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full bg-emerald-800 hover:bg-emerald-700 text-white font-bold px-6 py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formStatus === 'submitting' ? 'Sending...' : 'Start a Conversation'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Subtle separator before footer */}
      <div className="h-px bg-white/8"></div>
    </div>
  );
}
